import { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../../api/endpoints';
import type { DuplicateCluster, SyntaxCheckResult } from '../../../api/endpoints';
import { SyntaxTabs } from './SyntaxTabs';
import { ScopeBar } from './ScopeBar';
import { SyntaxPanel } from './SyntaxPanel';
import { DuplicatesPanel } from './DuplicatesPanel';
import { ApplyBanner } from './ApplyBanner';
import {
  buildDuplicateFixes,
  buildSyntaxFixes,
  duplicateMemberKey,
  expandSuggestions,
  groupRowsByFloorPlan,
  summarizeApply,
} from './util';
import type { ActiveTab, ApplyOutcome, PlanScope, SyntaxCheckModalProps } from './types';

export const SyntaxCheckModal = ({ open, selection, onClose, onApplied }: SyntaxCheckModalProps) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('syntax');
  const [scope, setScope] = useState<PlanScope>('all');

  const [syntaxLoading, setSyntaxLoading] = useState(false);
  const [syntaxLoaded, setSyntaxLoaded] = useState(false);
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [syntaxResult, setSyntaxResult] = useState<SyntaxCheckResult | null>(null);
  const [acceptedRowKeys, setAcceptedRowKeys] = useState<Set<string>>(new Set());
  const [editedTargets, setEditedTargets] = useState<Record<string, string>>({});

  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateLoaded, setDuplicateLoaded] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [clusters, setClusters] = useState<DuplicateCluster[]>([]);
  const [acceptedMemberKeys, setAcceptedMemberKeys] = useState<Set<string>>(new Set());
  const [editedCanonical, setEditedCanonical] = useState<Record<number, string>>({});

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyOutcome, setApplyOutcome] = useState<ApplyOutcome | null>(null);

  // Plans to check: the selection when scoped, else undefined (whole project).
  const scopedPlans = scope === 'selected' && selection.length > 0 ? selection : undefined;

  // An apply clears BOTH tabs: it renames the names each of them cached.
  const resetSyntaxState = useCallback(() => {
    setSyntaxLoaded(false);
    setSyntaxError(null);
    setSyntaxResult(null);
    setAcceptedRowKeys(new Set());
    setEditedTargets({});
  }, []);

  const resetDuplicateState = useCallback(() => {
    setDuplicateLoaded(false);
    setDuplicateError(null);
    setClusters([]);
    setAcceptedMemberKeys(new Set());
    setEditedCanonical({});
  }, []);

  const runSyntaxCheck = useCallback(async () => {
    setApplyOutcome(null);
    setSyntaxLoading(true);
    setSyntaxError(null);
    setSyntaxResult(null);
    const result = await api.syntaxCheckPlans(scopedPlans);
    setSyntaxLoading(false);
    setSyntaxLoaded(true);
    if (!result.ok || !result.data) {
      setSyntaxError(result.error ?? 'Syntax check failed');
      return;
    }
    const rows = expandSuggestions(result.data.suggestions);
    setSyntaxResult(result.data);
    setAcceptedRowKeys(new Set(rows.map((row) => row.key)));
    setEditedTargets({});
  }, [scopedPlans]);

  const runDuplicateCheck = useCallback(async () => {
    setApplyOutcome(null);
    setDuplicateLoading(true);
    setDuplicateError(null);
    setClusters([]);
    const result = await api.findDuplicatePlans(scopedPlans);
    setDuplicateLoading(false);
    setDuplicateLoaded(true);
    if (!result.ok || !result.data) {
      setDuplicateError(result.error ?? 'Duplicate search failed');
      return;
    }
    const memberKeys = new Set<string>();
    result.data.clusters.forEach((cluster, clusterIndex) => {
      cluster.members.forEach((_member, memberIndex) => {
        memberKeys.add(duplicateMemberKey(clusterIndex, memberIndex));
      });
    });
    setClusters(result.data.clusters);
    setAcceptedMemberKeys(memberKeys);
    setEditedCanonical({});
  }, [scopedPlans]);

  const toggleRow = useCallback((key: string) => {
    setAcceptedRowKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const editTarget = useCallback((key: string, value: string) => {
    setEditedTargets((previous) => ({ ...previous, [key]: value }));
  }, []);

  const toggleMember = useCallback((key: string) => {
    setAcceptedMemberKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const editCanonical = useCallback((clusterIndex: number, value: string) => {
    setEditedCanonical((previous) => ({ ...previous, [clusterIndex]: value }));
  }, []);

  const syntaxRows = useMemo(
    () => (syntaxResult ? expandSuggestions(syntaxResult.suggestions) : []),
    [syntaxResult],
  );
  const groupedSyntaxRows = useMemo(() => groupRowsByFloorPlan(syntaxRows), [syntaxRows]);
  const warnings = syntaxResult?.warnings ?? [];

  const syntaxFixes = useMemo(
    () => buildSyntaxFixes(syntaxRows, acceptedRowKeys, editedTargets),
    [syntaxRows, acceptedRowKeys, editedTargets],
  );
  const duplicateFixes = useMemo(
    () => buildDuplicateFixes(clusters, acceptedMemberKeys, editedCanonical),
    [clusters, acceptedMemberKeys, editedCanonical],
  );

  const activeFixes = activeTab === 'syntax' ? syntaxFixes : duplicateFixes;
  const activeRunning = activeTab === 'syntax' ? syntaxLoading : duplicateLoading;
  const activeHasRun = activeTab === 'syntax' ? syntaxLoaded : duplicateLoaded;

  const handleRun = useCallback(async () => {
    if (activeTab === 'syntax') {
      await runSyntaxCheck();
    } else {
      await runDuplicateCheck();
    }
  }, [activeTab, runSyntaxCheck, runDuplicateCheck]);

  const handleApply = useCallback(async () => {
    if (activeFixes.length === 0) return;
    setApplying(true);
    setApplyError(null);
    const result = await api.applySyntaxFixes(activeFixes);
    setApplying(false);
    if (!result.ok || !result.data) {
      setApplyError(result.error ?? 'Apply failed');
      return;
    }
    const outcome = summarizeApply(result.data);
    setApplyOutcome(outcome);
    onApplied();
    // Nothing landed — keep the rows so the reviewer can retry.
    if (outcome.planCount > 0) {
      resetSyntaxState();
      resetDuplicateState();
    }
  }, [activeFixes, onApplied, resetSyntaxState, resetDuplicateState]);

  // Reset to a clean, idle state whenever the modal opens. Nothing runs until
  // the reviewer picks a scope and clicks Run.
  useEffect(() => {
    if (!open) return;
    setActiveTab('syntax');
    setScope('all');
    resetSyntaxState();
    resetDuplicateState();
    setApplyError(null);
    setApplyOutcome(null);
  }, [open, resetSyntaxState, resetDuplicateState]);

  // A 'selected' scope with nothing selected would silently check the WHOLE project.
  useEffect(() => {
    if (selection.length === 0) setScope('all');
  }, [selection.length]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="syntax-modal" onClick={(event) => event.stopPropagation()}>
        <div className="syntax-header">
          <div>
            <h3>Syntax Check</h3>
            <div className="syntax-subtitle">Proofread group &amp; cable-type names on your floor plans</div>
          </div>
          <button className="ann-detail-close" onClick={onClose}>&times;</button>
        </div>
        {applyOutcome && (
          <ApplyBanner outcome={applyOutcome} onDismiss={() => setApplyOutcome(null)} />
        )}
        <SyntaxTabs activeTab={activeTab} onSelect={setActiveTab} />
        <ScopeBar
          scope={scope}
          selectedCount={selection.length}
          running={activeRunning}
          applying={applying}
          hasRun={activeHasRun}
          onScope={setScope}
          onRun={handleRun}
        />
        <div className="syntax-body">
          {activeTab === 'syntax' ? (
            <SyntaxPanel
              loading={syntaxLoading}
              loaded={syntaxLoaded}
              error={syntaxError}
              groups={groupedSyntaxRows}
              warnings={warnings}
              acceptedRowKeys={acceptedRowKeys}
              editedTargets={editedTargets}
              onToggleRow={toggleRow}
              onEditTarget={editTarget}
              onRetry={() => runSyntaxCheck()}
            />
          ) : (
            <DuplicatesPanel
              loading={duplicateLoading}
              loaded={duplicateLoaded}
              error={duplicateError}
              clusters={clusters}
              editedCanonical={editedCanonical}
              acceptedMemberKeys={acceptedMemberKeys}
              onEditCanonical={editCanonical}
              onToggleMember={toggleMember}
              onRetry={() => runDuplicateCheck()}
            />
          )}
        </div>
        <div className="syntax-footer">
          {applyError && <span className="syntax-error-text">{applyError}</span>}
          <button className="btn-review-action" onClick={onClose}>Cancel</button>
          <button
            className="btn-review-action btn-publish"
            disabled={applying || syntaxLoading || duplicateLoading || activeFixes.length === 0}
            onClick={() => handleApply()}
          >
            {applying ? (
              <>
                <span className="ann-btn-spinner" />
                Applying…
              </>
            ) : (
              `Apply ${activeFixes.length} selected`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
